<template>
	<label
		class="file-input input-file-validation"
		:class="systemMessage && 'error'"
	>
		<p class="margin-bottom-m">{{ label }}</p>
		<input
			ref="fileInput"
			type="file"
			:name="name"
			:accept="accept"
			:multiple="multiple"
			@change="checkFileSize"
		/>
		<span class="icon"></span>

		<span
			class="hint"
			v-if="fieldHint && !hideDetails && !systemMessage"
			v-html="fieldHint"
		></span>
		<span
			class="system-message"
			v-if="!hideDetails && (systemMessage || !fieldHint)"
			>{{ systemMessage || "&nbsp;" }}</span
		>
	</label>
</template>

<script>
import commonProps from "../common/commonProps";
import { mapActions, mapState } from "pinia";
import { useFormStore } from "~/pinia/platform/form/form.ts";

export default defineNuxtComponent({
	name: "TnInputFile",

	props: {
		...commonProps,
		label: String,
		types: Array,
		multiple: Boolean,
	},

	mounted() {
		if (this.required) {
			if (this.$refs.fileInput.files.length === 0) {
				this.addRequiredField();
			}
		}
	},

	data() {
		return {
			systemMessage: this.error || null,
			totalFileSize: 0,
			fieldHint: this.hint || (this.required && this.emptyError),
		};
	},

	computed: {
		...mapState(useFormStore, ["form"]),
		accept() {
			return this.types && this.types.join(",");
		},
	},

	methods: {
		...mapActions(useFormStore, [
			"addTotalFileSize",
			"subtractTotalFileSize",
			"addRequiredFormField",
			"removeRequiredFormField",
		]),
		checkFileSize() {
			//Check if any files are already uploaded
			if (this.totalFileSize > 0) {
				//Reduce the global totalFileSize with the previous file size
				this.subtractTotalFileSize(this.totalFileSize);
			}

			const files = this.$refs.fileInput.files;

			if (files.length === 0) {
				this.setSystemMessage(this.emptyError);
				return false;
			}

			let totalFileSize = 0;

			let cancel = false;
			files &&
				Object.values(files).forEach((file) => {
					if (file.size > this.$config.public.maxFileSize * 1024 * 1024) {
						//Remove the file and reset current local totalFileSize, cancel the counting of remaining files
						this.$refs.fileInput.value = "";
						totalFileSize = 0;
						this.setSystemMessage("Filen kan ikke være større enn " + this.$config.public.maxFileSize + " MB");
						cancel = true;
					} else {
						if (!cancel) {
							totalFileSize += file.size;
							this.clearSystemMessage();
						}
					}
				});

			if (this.form.totalFileSize + totalFileSize > this.$config.public.maxFileSize * 1024 * 1024) {
				//Remove the uploaded file and reset the local counter
				this.$refs.fileInput.value = "";
				totalFileSize = 0;
				this.setSystemMessage(
					"Den samlede filopplastningen kan ikke være større enn " + this.$config.public.maxFileSize + " MB",
				);
			} else {
				//Add the local current uploaded file sizes to the global counter
				this.addTotalFileSize(totalFileSize);
			}

			//Store the current uploaded file sizes locally
			this.totalFileSize = totalFileSize;
		},
		setSystemMessage(msg) {
			this.addRequiredField();
			this.systemMessage = msg;
		},
		clearSystemMessage() {
			this.removeRequiredFormField(this._uid);
			this.systemMessage = null;
		},
		validate() {
			this.checkFileSize();
		},
		addRequiredField() {
			this.addRequiredFormField({ uid: this._uid, elementId: this.$el.id, validate: this.validate });
		},
	},
});
</script>

<style lang="scss" scoped>
.file-input {
	input {
		font-size: initial;
	}

	p {
		font-weight: bold;

		@include font-text-s;
	}

	.system-message {
		color: $color-critical-500-core;
	}

	.hint,
	.system-message {
		display: block;
		font-size: 14px;
		line-height: 20px;
		margin-top: 10px;

		:deep(strong) {
			font-weight: bold;
		}
	}

	.hint {
		color: $color-text;

		:deep(a) {
			text-decoration: none;
			color: $color-cta-default;

			&:hover {
				border-bottom: 1px solid $color-cta-default;
			}
		}
	}

	.dark .hint {
		color: #fff;

		:deep(a) {
			text-decoration: none;
			color: $color-twe-cta-default;

			&:hover {
				border-bottom: 1px solid $color-twe-cta-default;
			}
		}
	}
}
</style>
